import { useEffect } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import { useTranslation } from 'react-i18next';
import { BackButton } from '../TopMenu/BackButton';
import {
  generatePlaybacksErrorData,
  handleErrorWithRedirect,
  shouldHandleErrorWithRedirect,
} from './utils';
import type { PlaybacksError } from '../store/iframe-controller';

const ErrorPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  padding-bottom: 56px;
  background-color: ${tokens.BRAND_COLORS.midnight_600};
`;

const ErrorPageTopBar = styled.div`
  display: flex;
  align-items: center;
  height: 55px;
  padding: 0 16px;
  text-align: initial;
`;

const ErrorPageContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  margin: 0 4px;
  background-color: ${tokens.BRAND_COLORS.neutral_900};
  border-radius: 4px;
  white-space: break-spaces;
`;

interface ErrorPageProps {
  error: PlaybacksError;
}

export const ErrorPage = ({ error }: ErrorPageProps): JSX.Element | null => {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.classList.add('side-panel-disabled');
    document.body.classList.add('playlist-disabled');
  }, []);

  if (shouldHandleErrorWithRedirect(error)) {
    handleErrorWithRedirect(error);

    return null;
  }

  const playbackData = generatePlaybacksErrorData(error);

  const {
    title,
    subtitle,
    buttonText,
    onButtonClick,
    icon,
    recording,
    isLoggedIn = false,
  } = playbackData;

  return (
    <ErrorPageContainer>
      <ErrorPageTopBar>
        <BackButton isDisabled={false} isLoggedIn={isLoggedIn} recording={recording} />
      </ErrorPageTopBar>
      <ErrorPageContent>
        <EmptyState
          title={t(title)}
          subtitle={t(subtitle)}
          buttonText={t(buttonText)}
          buttonKind="primary"
          onButtonClick={onButtonClick}
          icon={icon}
          colorsInversed
        />
      </ErrorPageContent>
    </ErrorPageContainer>
  );
};
